import { useCannectAssistant } from '@cannect/hooks/useCannectAssistant'
import { buildProductDetailsPath } from '@cannect/utils/productUrl'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { useTokenApi } from '@cannect/hooks/useTokenApi'
import { TProductDetailItem, useGetProductById } from '@cannect/services/resources/products'
import { BRAND_REMOVED_FROM_ANVISA_MESSAGE, LIST_BRAND_ATTENDANT } from '@cannect/constants/products'
import { RedirectUrls } from '@cannect/constants/texts'
import { useGetProductTiers } from '@cannect/services/resources/progressive-discounts'
import {
  useProductRecurrency,
  type RecurrencyChoice
} from '@cannect/new-components/organisms/Recurrency/RecurrencyPurchaseSelector/useProductRecurrency'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import api from 'services/api'
import { toast } from 'react-toastify'

export const useProductDetails = () => {
  const params = useParams() as any
  const productId = params?.id

  const { tokenApi } = useTokenApi()
  const history = useHistory()
  const location = useLocation()

  const { addToCart } = usePrepareCart()
  const { signedUser } = useSignedUser()
  const { prescription, setPath, setStep, addProductToPrescription } = useCannectAssistant()
  const { data, isFetching: isLoading } = useGetProductById(productId)

  const productDetails = useMemo(() => data?.product, [data?.product])

  const productIdNumber = productId ? Number(productId) : undefined
  const { data: tiersData } = useGetProductTiers(productIdNumber)

  const hasProgressiveDiscount = tiersData?.has_progressive_discount ?? false
  const progressiveTiers = tiersData?.tiers ?? []
  const progressiveRule = tiersData?.rule

  const fromLocation = {
    from: {
      pathName: location.pathname
    }
  }

  useEffect(() => {
    if (productDetails && productDetails.id) {
      const newUrl = buildProductDetailsPath(productDetails.brand?.name, productDetails.name, productDetails.id)

      if (window.location.pathname !== newUrl) {
        history.replace(newUrl)
      }
    }
  }, [productDetails, history])

  const [quantity, setQuantity] = useState(1)
  // Compra Recorrente (007): assinatura pré-selecionada quando o produto permite.
  const [recurrencyChoice, setRecurrencyChoice] = useState<RecurrencyChoice>({ mode: 'subscription', cycleDays: null })
  const productRecurrency = useProductRecurrency({
    productId: productIdNumber,
    quantity,
    cycleDays: recurrencyChoice.cycleDays
  })
  const subscriptionCycleDays =
    productRecurrency.state === 'available' && recurrencyChoice.mode === 'subscription'
      ? recurrencyChoice.cycleDays
      : null
  const [flavorModal, setFlavorModal] = useState(false)
  const [topicProductWarningModal, setTopicProductWarningModal] = useState<{
    open: boolean
    product?: TProductDetailItem
    quantity?: number
  }>({ open: false })
  const [expiringBatchWarningModal, setExpiringBatchWarningModal] = useState<{
    open: boolean
    product?: TProductDetailItem
    quantity?: number
  }>({ open: false })
  const [nutraceuticosModal, setNutraceuticosModal] = useState(false)
  const [anvisaModalMessage, setAnvisaModalMessage] = useState<string>('')
  const [modalUrlClickButton, setModalUrlClickButton] = useState<string>('')
  const [learnMore, setLearnMore] = useState<boolean>(false)
  const [prescriber, setPrescriber] = useState<boolean>(false)
  const [loadingToCart, setLoadingToCart] = useState(false)
  const isAssistantFlow = useMemo(() => location.pathname.startsWith('/produtos_assistente'), [location])

  const handleGoToLogin = () => {
    history.push({
      pathname: '/login',
      state: { from: { pathname: location.pathname } }
    })
  }

  const handleNotifyMeDetail = () => {
    if (!tokenApi) {
      sessionStorage.setItem(
        '@CANNECT:PENDING_NOTIFY_ME',
        JSON.stringify({ productId: productDetails?.id, source: 'portal_detail' })
      )
      handleGoToLogin()
      return
    }
    api
      .post(`/product/${productDetails?.id}/notify-me`, { source: 'portal_detail' })
      .then((res: any) => {
        const alreadyExisted = res?.data?.created === false
        toast.success(
          alreadyExisted
            ? 'Você já estava na lista de aviso para este produto.'
            : 'Você será avisado quando este produto estiver disponível.'
        )
      })
      .catch(() => {
        toast.error('Não foi possível registrar seu interesse. Tente novamente.')
      })
  }

  const handleAddToCart = async (
    id: number,
    quantity: number,
    class_id?: number,
    brand_id?: number,
    type?: string | undefined,
    is_cannabis?: boolean
  ) => {
    setLoadingToCart(true)
    try {
      await addToCart(id, quantity, class_id, brand_id, type, is_cannabis, subscriptionCycleDays ?? undefined)
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingToCart(false)
    }
  }

  const TOPIC_PRODUCT_WARNING_ID = 15757
  const TOPIC_PRODUCT_WARNING_MESSAGE =
    'ATENÇÃO: O produto USA Hemp - Full Spectrum - Tópico Miracle Heal é de uso TÓPICO. Em caso de dúvidas, consulte o profissional de saúde que acompanha seu tratamento.'

  const PRODUCTS_WITH_EXPIRING_BATCH = [15908, 15912, 15914, 15909]
  const EXPIRING_BATCH_WARNING_MESSAGE =
    'Atenção, cliente! O lote disponível deste produto possui vencimento em setembro. Ao adicionar ao carrinho, você está de acordo em receber esse lote. Agradecemos pela compreensão!'

  const requiresLoginAtAdd = (p: any) => p?.is_cannabis === true

  const handleAddToCartClick = async (product?: TProductDetailItem, quantity?: number) => {
    if (!product?.id || !quantity) return

    if (!tokenApi && requiresLoginAtAdd(product)) {
      handleGoToLogin()
      return
    }

    // Aviso tópico: produto 15757 — exibe modal informativo antes de seguir
    if (product.id === TOPIC_PRODUCT_WARNING_ID) {
      setTopicProductWarningModal({ open: true, product, quantity })
      return
    }

    if (PRODUCTS_WITH_EXPIRING_BATCH.includes(product.id)) {
      setExpiringBatchWarningModal({ open: true, product, quantity })
      return
    }

    const brandId = product?.brand?.id || product?.brand_id || 0

    if (LIST_BRAND_ATTENDANT.includes(brandId)) {
      setFlavorModal(true)
      setModalUrlClickButton(RedirectUrls.WHATSAPP_SALES)
      setLearnMore(true)
      setAnvisaModalMessage(BRAND_REMOVED_FROM_ANVISA_MESSAGE)
      return
    }

    handleAddToCart(
      product?.id,
      quantity,
      product?.class?.id,
      brandId,
      product?.type ?? undefined,
      (product as any)?.is_cannabis
    )
  }

  const redirectToProductPrescription = () => {
    if (!productDetails) return

    setPrescriber(true)

    setPath('alreadyKnow')
    setStep(2)
    const isProductAlreadyAdded = !!prescription.products.find((p) => p.id === productDetails.id)
    if (!isProductAlreadyAdded) {
      // @ts-expect-error type error, fix in function
      addProductToPrescription(productDetails)
    }
    history.push('/assistente')
  }

  const handleCloseFlavorModal = () => {
    setFlavorModal(false)
  }

  const handleChangeFlavor = (flavorId: number) => {
    if (!flavorId) return

    const currentPath = location.pathname
    const newPath = currentPath.replace(/\/\d+(?=[^/]*$)/, `/${flavorId}`)
    history.push(newPath)
  }

  const isPrescriber =
    signedUser?.prescriber?.council === 'CRM' ||
    signedUser?.prescriber?.council === 'CRO' ||
    signedUser?.prescriber?.council === 'RMS'

  const BRANDS_WITH_IMPORT_COST = ['Chuii Natur', 'BPI Labs']
  const isSupplementProduct =
    productDetails?.class?.id === 6 &&
    !BRANDS_WITH_IMPORT_COST.includes(productDetails?.brand?.name || '')

  return {
    fromLocation,
    productDetails,
    isLoading,
    redirectToProductPrescription,
    handleCloseFlavorModal,
    isPrescriber,
    nutraceuticosModal,
    setNutraceuticosModal,
    anvisaModalMessage,
    modalUrlClickButton,
    learnMore,
    prescriber,
    loadingToCart,
    setLoadingToCart,
    handleAddToCartClick,
    handleAddToCart,
    quantity,
    setQuantity,
    flavorModal,
    setFlavorModal,
    topicProductWarningModal,
    setTopicProductWarningModal,
    topicProductWarningMessage: TOPIC_PRODUCT_WARNING_MESSAGE,
    expiringBatchWarningModal,
    setExpiringBatchWarningModal,
    expiringBatchWarningMessage: EXPIRING_BATCH_WARNING_MESSAGE,
    handleChangeFlavor,
    isSupplementProduct,
    tokenApi,
    handleGoToLogin,
    handleNotifyMeDetail,
    hasProgressiveDiscount,
    progressiveTiers,
    progressiveRule,
    productRecurrency,
    recurrencyChoice,
    setRecurrencyChoice,
    subscriptionCycleDays
  }
}
